import { RefObject } from 'react';
import { OverlayScrollbarsComponent, OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import { OverlayScrollbars } from 'overlayscrollbars';

import { Box, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material';

import { CaseHistoryResponseDto } from '@bandido/shared/dtos';

import { MessageRenderer } from './components/MessageRenderer';
import { LoadingIndicator, MessageContainer } from './styled';
import { LoadingState } from './components/LoadingState';
import { MessageGroup } from './helpers';

interface CaseContentHistoryDesktopProps {
  // Data
  groupedMessages: MessageGroup[];
  isFetchingNextPage: boolean;
  isLoading: boolean;

  // Refs
  scrollRef: RefObject<OverlayScrollbarsComponentRef<'div'>>;
  topRef: RefObject<HTMLDivElement>;
  contentRef: RefObject<HTMLDivElement>;

  // Callbacks
  handleScroll: (_: OverlayScrollbars, event: Event) => void;
  isCurrentUser: (history: CaseHistoryResponseDto) => boolean;

  // Configuration
  contentHeight: string;
}

export const CaseContentHistoryDesktop: React.FC<CaseContentHistoryDesktopProps> = ({
  groupedMessages,
  isFetchingNextPage,
  isLoading,
  scrollRef,
  topRef,
  contentRef,
  handleScroll,
  isCurrentUser,
  contentHeight,
}) => {
  const theme = useTheme();

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <OverlayScrollbarsComponent
      ref={scrollRef}
      events={{ scroll: handleScroll }}
      options={{
        scrollbars: {
          autoHide: 'leave',
          theme: 'os-theme-light',
        },
      }}
      style={{
        overflow: 'hidden',
        height: contentHeight,
        padding: theme.spacing(0, 3),
      }}
    >
      <MessageContainer ref={contentRef}>
        <Box sx={{ height: 50, width: '100%' }} ref={topRef} />
        {isFetchingNextPage && (
          <LoadingIndicator>
            <CircularProgress size={24} />
          </LoadingIndicator>
        )}

        {groupedMessages.map((group, index) => {
          const previousGroup = index > 0 ? groupedMessages[index - 1] : null;
          const previousDate = previousGroup ? previousGroup.messages[0].createdAt : null;

          return (
            <MessageRenderer
              key={group.messages[0].id}
              group={group}
              lastMessageDate={previousDate}
              isCurrentUser={isCurrentUser}
            />
          );
        })}
      </MessageContainer>
    </OverlayScrollbarsComponent>
  );
};
